import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import set from 'date-fns/set';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import getDay from 'date-fns/getDay';
import { parseTranslatedDate } from "../../../../../../../shared/LanguageService";

// mapping of getDay from date-fns
export enum Weekday {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
}

export function getNextWorkday(date: Date): Date {
    let day = getDay(date);
    
    if (day == Weekday.Friday) {
        return addDays(date, 3);
    }
    
    if (day == Weekday.Saturday) {
        return addDays(date, 2);
    }
    
    return addDays(date, 1);
}

export function getNextDay(date: Date): Date {
    return addDays(date, 1);
}

export function getPreviousDay(date: Date): Date {
    return subDays(date, 1);
}

export function isPreviousDayTodayOrEarlier(currentDate: Date): boolean {
    let previousWorkday = getPreviousDay(currentDate);
    let today = new Date(Date.now());

    return isBefore(previousWorkday, today);
}

export type BookableDays = {
    // The day the screen opens on; null: open on the default day.
    firstBookableDate: Date | null;
    // No earlier day can be picked, a weekend or holiday can be this day; null: tomorrow.
    earliestBookableDate: Date | null;
};

// wantedDate is null until the customer picks a day.
export function getStartBookingDate(
    wantedDate: Date | null,
    defaultDate: Date,
    { firstBookableDate, earliestBookableDate }: BookableDays
): Date {
    if (wantedDate === null) {
        return firstBookableDate !== null ? firstBookableDate : defaultDate;
    }

    if (
        earliestBookableDate !== null &&
        isBefore(startOfDay(wantedDate), startOfDay(earliestBookableDate))
    ) {
        return earliestBookableDate;
    }

    return wantedDate;
}

export function isPreviousDayBeforeEarliestBookableDay(
    currentDate: Date,
    earliestBookableDate: Date | null
): boolean {
    if (earliestBookableDate === null) {
        return isPreviousDayTodayOrEarlier(currentDate);
    }

    return isBefore(startOfDay(getPreviousDay(currentDate)), startOfDay(earliestBookableDate));
}

enum CalendarItemType {
    Available = 0,
    SE360Booking = 1 << 0,
    ScandphotosBooking = 1 << 1,
    Blocked = 1 << 2,
    Travel = 1 << 3
}

type ICalendarItem = {
    orderId: number | null;
    end: Date;
    start: Date;
    type: CalendarItemType;
    userId: number; // But it's really the photographer Id
};

function createDateWithTime(date: Date, time: string): Date {
    let [hours, minutes] = time.split(':');
    return set(date, { hours: parseInt(hours), minutes: parseInt(minutes) });
}

export function createCalendarItem(photographerId: number, timeslot: string): ICalendarItem {
    let [time, dateString] = timeslot.split(';');
    let date = parseTranslatedDate(dateString, PHOTOGRAPHER_BOOKING_DATE_FORMAT);
    let [startTime, endTime] = time.split(' - ');

    let startDate = createDateWithTime(date, startTime);
    let endDate = createDateWithTime(date, endTime);

    return {
        orderId: null,
        end: endDate,
        start: startDate,
        type: CalendarItemType.SE360Booking,
        userId: photographerId // it was unfortunately named userId on the backend...
    };
}

export const PHOTOGRAPHER_BOOKING_DATE_FORMAT = 'E dd MMM yyyy';
